const LABEL_CACHE_LIMIT = 256;
const BUBBLE_CACHE_LIMIT = 128;

interface CachedLabelSprite {
    canvas: HTMLCanvasElement;
    logicalWidth: number;
    logicalHeight: number;
}

interface CachedBubbleSprite {
    canvas: HTMLCanvasElement;
    logicalWidth: number;
    bubbleHeight: number;
}

const labelCache = new Map<string, CachedLabelSprite>();
const bubbleCache = new Map<string, CachedBubbleSprite>();

let measurementCanvas: HTMLCanvasElement | null = null;
let measurementContext: CanvasRenderingContext2D | null = null;

function getMeasurementContext(): CanvasRenderingContext2D {
    if (measurementContext) {
        return measurementContext;
    }
    measurementCanvas = document.createElement('canvas');
    measurementContext = measurementCanvas.getContext('2d');
    if (!measurementContext) {
        throw new Error('Failed to create text measurement context');
    }
    return measurementContext;
}

function trimCache<T>(cache: Map<string, T>, limit: number): void {
    while (cache.size > limit) {
        const firstKey = cache.keys().next().value;
        if (!firstKey) {
            break;
        }
        cache.delete(firstKey);
    }
}

function getCacheScale(): number {
    if (typeof window === 'undefined' || !Number.isFinite(window.devicePixelRatio)) {
        return 1;
    }
    return Math.max(1, window.devicePixelRatio);
}

function normalizeFontPx(fontPx: number): number {
    return Math.round(fontPx * 100) / 100;
}

function labelCacheKey(text: string, fontPx: number, color: string, cacheScale: number): string {
    return `${normalizeFontPx(fontPx)}:${color}:${cacheScale}:${text}`;
}

function bubbleCacheKey(text: string, fontPx: number, maxWidth: number, cacheScale: number): string {
    return `${normalizeFontPx(fontPx)}:${maxWidth}:${cacheScale}:${text}`;
}

function buildLabelSprite(text: string, fontPx: number, color: string, cacheScale: number): CachedLabelSprite {
    const measure = getMeasurementContext();
    measure.font = `${fontPx}px Arial, sans-serif`;
    const textMetrics = measure.measureText(text);
    const logicalWidth = Math.max(1, Math.ceil(textMetrics.width) + 2);
    const logicalHeight = Math.max(1, Math.ceil(fontPx * 1.35));
    const width = Math.max(1, Math.ceil(logicalWidth * cacheScale));
    const height = Math.max(1, Math.ceil(logicalHeight * cacheScale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.style.width = `${logicalWidth}px`;
    canvas.style.height = `${logicalHeight}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Failed to create label sprite context');
    }
    ctx.setTransform(cacheScale, 0, 0, cacheScale, 0, 0);
    ctx.font = `${fontPx}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = color;
    ctx.fillText(text, logicalWidth / 2, logicalHeight / 2);
    return {
        canvas,
        logicalWidth,
        logicalHeight,
    };
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number): void {
    const r = Math.min(radius, width / 2, height / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + width, y, x + width, y + height, r);
    ctx.arcTo(x + width, y + height, x, y + height, r);
    ctx.arcTo(x, y + height, x, y, r);
    ctx.arcTo(x, y, x + width, y, r);
    ctx.closePath();
}

function wrapBubbleText(text: string, fontPx: number, maxWidth: number): { lines: string[]; textWidth: number } {
    const measure = getMeasurementContext();
    measure.font = `${fontPx}px Arial, sans-serif`;
    const words = text.split(/\s+/);
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
        const nextLine = currentLine ? `${currentLine} ${word}` : word;
        if (measure.measureText(nextLine).width <= maxWidth || currentLine.length === 0) {
            currentLine = nextLine;
            continue;
        }
        lines.push(currentLine);
        currentLine = word;
    }

    if (currentLine) {
        lines.push(currentLine);
    }

    const textWidth = Math.min(
        maxWidth,
        lines.reduce((max, line) => Math.max(max, measure.measureText(line).width), 0)
    );

    return { lines, textWidth };
}

function buildSpeechBubbleSprite(text: string, fontPx: number, maxWidth: number, cacheScale: number): CachedBubbleSprite {
    const paddingX = 8;
    const paddingY = 6;
    const tailHeight = 8;
    const lineHeight = Math.max(14, fontPx * 1.15);
    const { lines, textWidth } = wrapBubbleText(text, fontPx, maxWidth);
    const bubbleWidth = Math.max(1, Math.ceil(textWidth + paddingX * 2));
    const bubbleHeight = Math.max(1, Math.ceil(lines.length * lineHeight + paddingY * 2));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.ceil(bubbleWidth * cacheScale));
    canvas.height = Math.max(1, Math.ceil((bubbleHeight + tailHeight) * cacheScale));
    canvas.style.width = `${bubbleWidth}px`;
    canvas.style.height = `${bubbleHeight + tailHeight}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Failed to create speech bubble sprite context');
    }
    ctx.setTransform(cacheScale, 0, 0, cacheScale, 0, 0);
    ctx.font = `${fontPx}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    roundRect(ctx, 0, 0, bubbleWidth, bubbleHeight, 8);
    ctx.fillStyle = 'rgba(15, 23, 42, 0.9)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(226, 232, 240, 0.75)';
    ctx.lineWidth = 1;
    ctx.stroke();

    const bubbleCenterX = bubbleWidth / 2;
    ctx.beginPath();
    ctx.moveTo(bubbleCenterX - 7, bubbleHeight);
    ctx.lineTo(bubbleCenterX, bubbleHeight + tailHeight);
    ctx.lineTo(bubbleCenterX + 7, bubbleHeight);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = 'rgba(248, 250, 252, 1)';
    lines.forEach((line, index) => {
        const textY = paddingY + lineHeight * index + lineHeight / 2;
        ctx.fillText(line, bubbleCenterX, textY);
    });

    return {
        canvas,
        logicalWidth: bubbleWidth,
        bubbleHeight,
    };
}

export function drawCachedLabel(
    ctx: CanvasRenderingContext2D,
    options: {
        text: string;
        centerX: number;
        centerY: number;
        fontPx: number;
        color?: string;
        alpha?: number;
    }
): void {
    const { text, centerX, centerY, fontPx } = options;
    const color = options.color ?? '#ffffff';
    const alpha = options.alpha ?? 1;
    const cacheScale = getCacheScale();
    const key = labelCacheKey(text, fontPx, color, cacheScale);
    let sprite = labelCache.get(key);
    if (!sprite) {
        sprite = buildLabelSprite(text, fontPx, color, cacheScale);
        labelCache.set(key, sprite);
        trimCache(labelCache, LABEL_CACHE_LIMIT);
    }

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.drawImage(
        sprite.canvas,
        centerX - sprite.logicalWidth / 2,
        centerY - sprite.logicalHeight / 2,
        sprite.logicalWidth,
        sprite.logicalHeight,
    );
    ctx.restore();
}

export function drawCachedSpeechBubble(
    ctx: CanvasRenderingContext2D,
    options: {
        text: string;
        centerX: number;
        baselineY: number;
        fontPx: number;
        maxWidth: number;
        alpha?: number;
    }
): void {
    const { text, centerX, baselineY, fontPx, maxWidth } = options;
    const alpha = options.alpha ?? 1;
    const cacheScale = getCacheScale();
    const key = bubbleCacheKey(text, fontPx, maxWidth, cacheScale);
    let sprite = bubbleCache.get(key);
    if (!sprite) {
        sprite = buildSpeechBubbleSprite(text, fontPx, maxWidth, cacheScale);
        bubbleCache.set(key, sprite);
        trimCache(bubbleCache, BUBBLE_CACHE_LIMIT);
    }

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.drawImage(
        sprite.canvas,
        centerX - sprite.logicalWidth / 2,
        baselineY - sprite.bubbleHeight,
        sprite.logicalWidth,
        sprite.canvas.height / cacheScale,
    );
    ctx.restore();
}

export function clearTextSpriteCache(): void {
    labelCache.clear();
    bubbleCache.clear();
}

export function getCachedLabelSize(text: string, fontPx: number, color = '#ffffff'): { width: number; height: number } {
    const cacheScale = getCacheScale();
    const key = labelCacheKey(text, fontPx, color, cacheScale);
    let sprite = labelCache.get(key);
    if (!sprite) {
        sprite = buildLabelSprite(text, fontPx, color, cacheScale);
        labelCache.set(key, sprite);
        trimCache(labelCache, LABEL_CACHE_LIMIT);
    }
    return {
        width: sprite.logicalWidth,
        height: sprite.logicalHeight,
    };
}

export function getCachedSpeechBubbleSize(text: string, fontPx: number, maxWidth: number): { width: number; height: number; bubbleHeight: number } {
    const cacheScale = getCacheScale();
    const key = bubbleCacheKey(text, fontPx, maxWidth, cacheScale);
    let sprite = bubbleCache.get(key);
    if (!sprite) {
        sprite = buildSpeechBubbleSprite(text, fontPx, maxWidth, cacheScale);
        bubbleCache.set(key, sprite);
        trimCache(bubbleCache, BUBBLE_CACHE_LIMIT);
    }
    return {
        width: sprite.logicalWidth,
        height: sprite.logicalHeight,
        bubbleHeight: sprite.bubbleHeight,
    };
}
